import { useInfiniteQuery } from '@tanstack/react-query';

import { chamarApi } from '@/nucleo/auth/cliente-api';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';

import { CAMINHO_DA_API, CHAVES } from './useDocs';

/** Uma versão salva do artigo. O texto inteiro não vem: só a prévia. */
export interface DocVersion {
  id: string;
  /** Sequencial por artigo; é o que "Restaurar" informa à API. */
  numero: number;
  previa: string;
  /** Tamanho do texto inteiro, para saber se a prévia foi cortada. */
  caracteres: number;
  created_at: string;
  /** A versão vigente do artigo: restaurá-la não mudaria nada. */
  atual: boolean;
}

interface VersaoDaApi {
  id: string;
  numero: number;
  previa: string;
  caracteres: number;
  criadoEm: string;
  atual: boolean;
}

interface ListaDaApi {
  itens: VersaoDaApi[];
  total: number;
  pagina: number;
  tamanho: number;
}

const TAMANHO_DA_PAGINA = 20;

/** Histórico do artigo, da versão mais nova para a mais antiga, uma página por vez. */
export function useDocsVersions(articleId: string | null, aberto = true) {
  const consulta = useInfiniteQuery({
    queryKey: CHAVES.versoes(articleId ?? ''),
    queryFn: ({ pageParam }) =>
      chamarApi<ListaDaApi>(
        `${CAMINHO_DA_API}/${articleId}/versoes?pagina=${pageParam}&tamanho=${TAMANHO_DA_PAGINA}`,
      ),
    initialPageParam: 1,
    getNextPageParam: (ultima, todas) => {
      const carregadas = todas.reduce((n, p) => n + p.itens.length, 0);
      return ultima.itens.length > 0 && carregadas < ultima.total ? ultima.pagina + 1 : undefined;
    },
    // Só consulta com a gaveta aberta.
    enabled: !!articleId && aberto,
    retry: naoInsistirEmRecusa,
  });

  const versions: DocVersion[] = (consulta.data?.pages ?? []).flatMap((p) =>
    p.itens.map((v) => ({
      id: v.id,
      numero: v.numero,
      previa: v.previa,
      caracteres: v.caracteres,
      created_at: v.criadoEm,
      atual: v.atual,
    })),
  );

  return {
    versions,
    loading: consulta.isLoading,
    error: consulta.error,
    refetch: consulta.refetch,
    hasMore: consulta.hasNextPage,
    loadingMore: consulta.isFetchingNextPage,
    loadMore: () => void consulta.fetchNextPage(),
  };
}
